/**
 * Field Path Resolver Composable
 *
 * Provides a single source of truth for resolving field paths based on model and is_extended flag.
 * This ensures consistent field mapping across checkout and registration flows.
 *
 * @returns {Object} - Object containing getFieldPath function
 */
export function useFieldPathResolver() {
  /**
   * Get the proper path for a field based on its model and is_extended flag
   *
   * @param {Object} field - Field definition with model and is_extended properties
   * @returns {string|null} - Full path for the field value in form data structure
   *
   * @example
   * // Contact field (non-extended)
   * getFieldPath({ field: { key: 'first_name', model: 'contact' }, is_extended: false })
   * // Returns: 'contact.first_name'
   *
   * @example
   * // Contact field (extended)
   * getFieldPath({ field: { key: 'dietary_requirements', model: 'contact' }, is_extended: true })
   * // Returns: 'contact.extended_fields.dietary_requirements'
   *
   * @example
   * // Guest field
   * getFieldPath({ field: { key: 'shirt_size', model: 'guest' }, is_extended: false })
   * // Returns: 'extended_fields.shirt_size'
   *
   * @example
   * // Booking field
   * getFieldPath({ field: { key: 'table_preference', model: 'booking' }, is_extended: false })
   * // Returns: 'booking.extended_fields.table_preference'
   */
  const getFieldPath = (field) => {
    if (!field?.field) return null

    const key = field.field.key
    const model = field.field.model || 'contact'
    const isExtended = field.field.is_extended || false

    if (model === 'booking') {
      return isExtended ? `booking.extended_fields.${key}` : `booking.${key}`
    }

    if (model === 'contact') {
      return isExtended ? `extended_fields.${key}` : `contact.${key}`
    }
    
    if (model === 'guest') {
      return `extended_fields.${key}`
    }
    

    // Default to contact for unknown models
    return `contact.${key}`
  }

  return { getFieldPath }
}
